/**
 * Rail-head detection on a cross-section slice of the scan.
 *
 * A slice is the points within ±`halfLength` along a heading around a centre,
 * in local coordinates: u along the heading, v across it (positive = to the
 * RIGHT of the heading), z as scanned. A rail head is a narrow ridge: its top
 * band stands clear of the sleepers / ballast beside it. Mobile-mapping scans
 * put only one or two points on a 70 mm head per scan line, so the slice is
 * a metre long (several scan lines) and every test is on medians, never on a
 * single point.
 */
import type { PointGrid } from './pointGrid';

export interface XYZ {
  x: number;
  y: number;
  z: number;
}

/** Track heading in XY (unit vector). */
export interface Heading {
  dx: number;
  dy: number;
}

export interface Slice {
  u: Float64Array;
  v: Float64Array;
  z: Float64Array;
  n: number;
}

/** UIC 60 / 54E1 rail head width, m: head-centre spacing minus this ≈ the gauge. */
export const RAIL_HEAD_WIDTH_M = 0.072;
/** Standard gauge, m. */
export const STANDARD_GAUGE_M = 1.435;
/** Head-centre spacing of a standard-gauge pair, m. */
export const STANDARD_HEAD_SPACING_M = STANDARD_GAUGE_M + RAIL_HEAD_WIDTH_M;
/** Head-centre spacings accepted when the track is first found (narrow to broad gauge), m. */
export const MIN_HEAD_SPACING_M = 0.8;
export const MAX_HEAD_SPACING_M = 1.75;

/** How far the head stands above what is beside it, at least (m). */
const MIN_PROMINENCE_M = 0.05;
/** Top band of a head: points this far under its top still belong to it (m). */
const HEAD_BAND_M = 0.025;
/** A head is narrower than this across (m, p10-p90 of its top band). */
const MAX_HEAD_SPREAD_M = 0.1;
/** Where "beside the head" is measured: this far from its centre (m). */
const BESIDE_MIN_M = 0.12;
const BESIDE_MAX_M = 0.35;

export const rightOf = (h: Heading) => ({ nx: h.dy, ny: -h.dx });

/** Growable Float64 scratch buffer (the hot loops reuse these instead of allocating number[]s). */
class Buf {
  a = new Float64Array(1024);
  n = 0;
  push(x: number) {
    if (this.n === this.a.length) {
      const b = new Float64Array(this.a.length * 2);
      b.set(this.a);
      this.a = b;
    }
    this.a[this.n++] = x;
  }
}

const sliceU = new Buf();
const sliceV = new Buf();
const sliceZ = new Buf();

const takeSlice = (): Slice => ({
  u: sliceU.a.slice(0, sliceU.n),
  v: sliceV.a.slice(0, sliceV.n),
  z: sliceZ.a.slice(0, sliceZ.n),
  n: sliceU.n,
});

/** Points within ±halfLength along and ±halfWidth across, z within [zMin, zMax]. */
export function sliceAt(
  grid: PointGrid,
  centre: XYZ,
  heading: Heading,
  halfLength: number,
  halfWidth: number,
  zMin: number,
  zMax: number,
): Slice {
  const { nx, ny } = rightOf(heading);
  const { dx, dy } = heading;
  const cx = centre.x;
  const cy = centre.y;
  sliceU.n = sliceV.n = sliceZ.n = 0;
  // The rectangle's XY bounding box (a hair wider: the filter below decides).
  const ex0 = Math.abs(dx) * halfLength + Math.abs(nx) * halfWidth + 1e-6;
  const ey0 = Math.abs(dy) * halfLength + Math.abs(ny) * halfWidth + 1e-6;
  grid.forEachCellIn(cx - ex0, cy - ey0, cx + ex0, cy + ey0, (xyz, from, to) => {
    for (let i = from; i < to; i++) {
      const pz = xyz[i * 3 + 2];
      if (pz < zMin || pz > zMax) continue;
      const ex = xyz[i * 3] - cx;
      const ey = xyz[i * 3 + 1] - cy;
      const pu = ex * dx + ey * dy;
      if (pu < -halfLength || pu > halfLength) continue;
      const pv = ex * nx + ey * ny;
      if (pv < -halfWidth || pv > halfWidth) continue;
      sliceU.push(pu);
      sliceV.push(pv);
      sliceZ.push(pz);
    }
  });
  return takeSlice();
}

/**
 * The points around `centre` once, for many slices through it at different
 * headings (`findTrack`'s sweep): offsets from the centre within the XY square
 * of half-side `r`, z within [zMin, zMax].
 */
interface Neighbourhood {
  ex: Float64Array;
  ey: Float64Array;
  z: Float64Array;
  n: number;
}

function neighbourhood(grid: PointGrid, centre: XYZ, r: number, zMin: number, zMax: number): Neighbourhood {
  const ex = new Buf();
  const ey = new Buf();
  const zz = new Buf();
  grid.forEachCellIn(centre.x - r, centre.y - r, centre.x + r, centre.y + r, (xyz, from, to) => {
    for (let i = from; i < to; i++) {
      const pz = xyz[i * 3 + 2];
      if (pz < zMin || pz > zMax) continue;
      ex.push(xyz[i * 3] - centre.x);
      ey.push(xyz[i * 3 + 1] - centre.y);
      zz.push(pz);
    }
  });
  return { ex: ex.a, ey: ey.a, z: zz.a, n: ex.n };
}

/** `sliceAt` on a neighbourhood: the same points, the same values; `uCentre` moves it along the heading. */
function sliceOf(nb: Neighbourhood, heading: Heading, halfLength: number, halfWidth: number, uCentre = 0): Slice {
  const { nx, ny } = rightOf(heading);
  const { dx, dy } = heading;
  sliceU.n = sliceV.n = sliceZ.n = 0;
  for (let i = 0; i < nb.n; i++) {
    const ex = nb.ex[i];
    const ey = nb.ey[i];
    const pu = ex * dx + ey * dy - uCentre;
    if (pu < -halfLength || pu > halfLength) continue;
    const pv = ex * nx + ey * ny;
    if (pv < -halfWidth || pv > halfWidth) continue;
    sliceU.push(pu);
    sliceV.push(pv);
    sliceZ.push(nb.z[i]);
  }
  return takeSlice();
}

const quantile = (sorted: ArrayLike<number>, q: number) => {
  if (sorted.length === 0) return NaN;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
};
const sortedOf = (a: number[]) => Float64Array.from(a).sort();
const median = (a: number[]) => quantile(sortedOf(a), 0.5);

/** Put the k-th smallest of a[0 .. n) at a[k] (smaller before, larger after); reorders a. */
function selectKth(a: Float64Array, n: number, k: number) {
  let lo = 0;
  let hi = n - 1;
  while (hi > lo) {
    const mid = (lo + hi) >> 1;
    // Median of three as the pivot.
    if (a[mid] < a[lo]) swap(a, mid, lo);
    if (a[hi] < a[lo]) swap(a, hi, lo);
    if (a[hi] < a[mid]) swap(a, hi, mid);
    const pivot = a[mid];
    let i = lo;
    let j = hi;
    while (i <= j) {
      while (a[i] < pivot) i++;
      while (a[j] > pivot) j--;
      if (i <= j) {
        swap(a, i, j);
        i++;
        j--;
      }
    }
    if (k <= j) hi = j;
    else if (k >= i) lo = i;
    else return;
  }
}
function swap(a: Float64Array, i: number, j: number) {
  const t = a[i];
  a[i] = a[j];
  a[j] = t;
}

/** `quantile` of a[0 .. n) as if sorted, by selection (the same value; reorders a). */
function quantileSel(a: Float64Array, n: number, q: number): number {
  if (n === 0) return NaN;
  const pos = (n - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  selectKth(a, n, lo);
  const vLo = a[lo];
  let vHi = vLo;
  if (hi !== lo) {
    // After the selection everything past lo is >= a[lo]: the next one up is their minimum.
    vHi = a[lo + 1];
    for (let i = lo + 2; i < n; i++) if (a[i] < vHi) vHi = a[i];
  }
  return vLo + (vHi - vLo) * (pos - lo);
}

export interface RailHit {
  /** Across-track position of the head centre (m, slice frame). */
  v: number;
  /** Rail-top height (m). */
  z: number;
  /** Height of the head above what is beside it (m). */
  prominence: number;
  points: number;
  /** p10-p90 across-track spread of the head's top band (m): small when the slice is square to the rail. */
  spread: number;
  /**
   * The head's side faces at the gauge-measuring depth (14 mm under the top):
   * across-track extent of the points there (3rd / 97th percentile), or null
   * when too few points reach that band to tell (a sparse scan).
   */
  edges: [number, number] | null;
}

/** Track gauge is measured this far below the rail top (EN 13848-1). */
export const GAUGE_DEPTH_M = 0.014;
/** Points needed in the gauge band before its edges are trusted. */
const MIN_EDGE_POINTS = 12;

/**
 * The rail head nearest `v0`: searched within ±`searchM` across and
 * ±`dzM` in height around `z0`. Null when nothing there stands out as a head.
 */
const innerZ = new Buf();
const innerV = new Buf();
const headV = new Buf();
const headZ = new Buf();
const beside = new Buf();
const bandV = new Buf();

export function findHead(slice: Slice, v0: number, z0: number, searchM = 0.12, dzM = 0.15): RailHit | null {
  innerZ.n = innerV.n = 0;
  for (let i = 0; i < slice.n; i++) {
    if (Math.abs(slice.v[i] - v0) <= searchM && Math.abs(slice.z[i] - z0) <= dzM) {
      innerZ.push(slice.z[i]);
      innerV.push(slice.v[i]);
    }
  }
  const nInner = innerZ.n;
  if (nInner < 3) return null;
  const iz = innerZ.a;
  const iv = innerV.a;
  const top = denseTop(iz, nInner);
  if (top == null) return null;
  headV.n = headZ.n = 0;
  for (let i = 0; i < nInner; i++) {
    // Above the top are stray returns (a leaf, a wire, a bird), not the head.
    if (iz[i] >= top - HEAD_BAND_M && iz[i] <= top + TOP_LAYER_M) {
      headV.push(iv[i]);
      headZ.push(iz[i]);
    }
  }
  let nHead = headV.n;
  if (nHead < 2) return null;
  let sv = headV.a.subarray(0, nHead).sort();
  let spread = quantile(sv, 0.9) - quantile(sv, 0.1);
  // Too wide for one head: only then look for two (a turnout) and keep the one nearest v0.
  let vLo = -Infinity;
  let vHi = Infinity;
  if (spread > MAX_HEAD_SPREAD_M) {
    const run = nearestHeadRun(sv, v0);
    if (!run) return null;
    [vLo, vHi] = [sv[run[0]], sv[run[1] - 1]];
    sv = sv.subarray(run[0], run[1]);
    nHead = sv.length;
    spread = quantile(sv, 0.9) - quantile(sv, 0.1);
    headZ.n = 0;
    for (let i = 0; i < nInner; i++) {
      if (iz[i] >= top - HEAD_BAND_M && iz[i] <= top + TOP_LAYER_M && iv[i] >= vLo && iv[i] <= vHi) headZ.push(iz[i]);
    }
  }
  const v = quantile(sv, 0.5);
  const z = quantileSel(headZ.a, nHead, 0.5);
  beside.n = 0;
  for (let i = 0; i < slice.n; i++) {
    const d = Math.abs(slice.v[i] - v);
    if (d >= BESIDE_MIN_M && d <= BESIDE_MAX_M && slice.z[i] <= z + HEAD_BAND_M && slice.z[i] >= z - 0.6) {
      beside.push(slice.z[i]);
    }
  }
  if (beside.n < 3) return null;
  const prominence = z - quantileSel(beside.a, beside.n, 0.5);
  if (prominence < MIN_PROMINENCE_M) return null;
  bandV.n = 0;
  for (let i = 0; i < nInner; i++) {
    if (iz[i] >= top - GAUGE_DEPTH_M && iz[i] <= top + TOP_LAYER_M && iv[i] >= vLo - 0.005 && iv[i] <= vHi + 0.005) {
      bandV.push(iv[i]);
    }
  }
  let edges: [number, number] | null = null;
  if (bandV.n >= MIN_EDGE_POINTS) {
    const sb = bandV.a.subarray(0, bandV.n).sort();
    const e: [number, number] = [quantile(sb, 0.03), quantile(sb, 0.97)];
    // A head is 55-85 mm wide at that depth; anything else is not two clean faces.
    if (e[1] - e[0] >= 0.045 && e[1] - e[0] <= 0.09) edges = e;
  }
  return { v, z, prominence, points: nHead, spread, edges };
}

/** A gap this wide across a too-wide head band splits it into two heads (m). */
const HEAD_GAP_M = 0.04;
/** Two heads whose distances to v0 differ by less than this: no telling which is followed (m). */
const HEAD_AMBIGUOUS_M = 0.02;

/**
 * Two heads in one search window — at a turnout the switch rail opens beside its stock rail, the
 * diverging rail runs a few cm off the through one — read as one too-wide head. Split the sorted
 * across positions `sv` at gaps of `HEAD_GAP_M` and return the run [from, to) nearest `v0` when
 * it is head-sized and clearly nearer than any other; else null (one run only: nothing to split).
 * Only called on a band already too wide for one head.
 */
function nearestHeadRun(sv: Float64Array, v0: number): [number, number] | null {
  let runs = 0;
  let best: [number, number] | null = null;
  let bestD = Infinity;
  let secondD = Infinity;
  let from = 0;
  for (let k = 1; k <= sv.length; k++) {
    if (k < sv.length && sv[k] - sv[k - 1] < HEAD_GAP_M) continue;
    if (k - from >= 2) {
      runs++;
      const d = Math.abs(sv[(from + k - 1) >> 1] - v0);
      if (d < bestD) {
        secondD = bestD;
        [bestD, best] = [d, [from, k]];
      } else if (d < secondD) secondD = d;
    }
    from = k;
  }
  if (runs < 2 || !best || secondD - bestD < HEAD_AMBIGUOUS_M) return null;
  if (sv[best[1] - 1] - sv[best[0]] > MAX_HEAD_SPREAD_M) return null;
  return best;
}

/** Thickness of the "top layer" a head's top is looked for in (m). */
const TOP_LAYER_M = 0.015;
/** Share of a window's points the top layer must hold (a head carries far more; strays far fewer). */
const TOP_SUPPORT_SHARE = 0.08;

/**
 * The top of the highest DENSE layer of heights: the highest z with at least
 * max(3, 8 % of the points) within `TOP_LAYER_M` under it. Stray returns above
 * a rail (vegetation, wires, people, multipath) are few in any 15 mm layer, so
 * they never set the top the way a max or a high percentile lets them.
 */
const topScratch = new Buf();

export function denseTop(zs: ArrayLike<number>, count = zs.length): number | null {
  if (count < 3) return null;
  if (topScratch.a.length < count) topScratch.a = new Float64Array(Math.max(count, topScratch.a.length * 2));
  const asc = topScratch.a.subarray(0, count);
  for (let i = 0; i < count; i++) asc[i] = zs[i];
  asc.sort();
  // Walked from the top down: desc[i] = asc[last - i].
  const last = count - 1;
  const need = Math.max(3, Math.ceil(count * TOP_SUPPORT_SHARE));
  let j = 0;
  for (let i = 0; i < count; i++) {
    if (j < i) j = i;
    while (j + 1 < count && asc[last - i] - asc[last - j - 1] <= TOP_LAYER_M) j++;
    if (j - i + 1 >= need) return asc[last - i];
  }
  return null;
}

/** A pair of heads found with no prior (the first click). */
export interface TrackFix {
  centre: XYZ;
  heading: Heading;
  left: RailHit;
  right: RailHit;
  score: number;
  /** The headings weighed (the debug log): sweep score, and the share of the heads found again along it. */
  candidates: { deg: number; score: number; continues: number }[];
}

const BIN_M = 0.02;

/** Narrow ridges of a slice's per-bin top height: candidate heads, no prior. */
export function ridgesOf(
  slice: Slice,
  halfWidth: number,
): { v: number; z: number; prominence: number; width: number }[] {
  const nb = Math.ceil((2 * halfWidth) / BIN_M);
  // Per bin, the top of its dense layer (not its max: one stray return per bin would make a ridge).
  const perBin: number[][] = Array.from({ length: nb }, () => []);
  for (let i = 0; i < slice.n; i++) {
    const b = Math.floor((slice.v[i] + halfWidth) / BIN_M);
    if (b >= 0 && b < nb) perBin[b].push(slice.z[i]);
  }
  const top = new Float64Array(nb).fill(NaN);
  for (let b = 0; b < nb; b++) {
    const zs = perBin[b];
    if (zs.length >= 3) top[b] = denseTop(zs) ?? NaN;
    else if (zs.length > 0) top[b] = Math.min(...zs);
  }
  const besideBins = (b: number) => {
    const out: number[] = [];
    const lo = Math.round(BESIDE_MIN_M / BIN_M);
    const hi = Math.round(BESIDE_MAX_M / BIN_M);
    for (let d = lo; d <= hi; d++) {
      for (const k of [b - d, b + d]) if (k >= 0 && k < nb && Number.isFinite(top[k])) out.push(top[k]);
    }
    return out;
  };
  // Peaks, not runs of raised bins: on a dense scan the rail foot and the
  // fastenings beside the head stand clear of the ballast too, and a run
  // spanning them is far wider than a head.
  const ridges: { v: number; z: number; prominence: number; width: number }[] = [];
  const peakReach = 3; // bins: a peak is the highest within ±6 cm
  let skipTo = -1;
  for (let b = 0; b < nb; b++) {
    if (b <= skipTo || !Number.isFinite(top[b])) continue;
    let isPeak = true;
    for (let k = Math.max(0, b - peakReach); k <= Math.min(nb - 1, b + peakReach) && isPeak; k++) {
      if (top[k] > top[b]) isPeak = false;
    }
    if (!isPeak) continue;
    const bes = besideBins(b);
    if (bes.length < 3) continue;
    const prominence = top[b] - median(bes);
    if (prominence < MIN_PROMINENCE_M) continue;
    // The head: the bins around the peak within its top band (an empty bin does not end it).
    const floor = top[b] - 0.03;
    const inBand = (k: number) => k >= 0 && k < nb && top[k] >= floor;
    let lo = b;
    while (inBand(lo - 1) || (lo - 2 >= 0 && !Number.isFinite(top[lo - 1]) && inBand(lo - 2)))
      lo -= inBand(lo - 1) ? 1 : 2;
    let hi = b;
    while (inBand(hi + 1) || (hi + 2 < nb && !Number.isFinite(top[hi + 1]) && inBand(hi + 2)))
      hi += inBand(hi + 1) ? 1 : 2;
    skipTo = hi;
    let wSum = 0;
    let vSum = 0;
    for (let j = lo; j <= hi; j++) {
      if (!inBand(j)) continue;
      wSum += 1;
      vSum += (j + 0.5) * BIN_M - halfWidth;
    }
    ridges.push({ v: vSum / wSum, z: top[b], prominence, width: (hi - lo + 1) * BIN_M });
  }
  return ridges.filter((r) => r.width <= 0.12);
}

/**
 * The best head pair in a slice taken at a trial heading; the click must be on
 * a head or between them. Scored on the heads' own top bands: many points,
 * tightly across (a slice not square to the rails smears each head), standing
 * well clear, at a spacing near `headSpacingM` (standard gauge unless the user set another).
 */
function bestPair(slice: Slice, halfWidth: number, headSpacingM: number) {
  const ridges = ridgesOf(slice, halfWidth);
  // Each ridge's head once (it is the same in every pair it is tried in).
  const heads: (RailHit | null | undefined)[] = new Array(ridges.length);
  const headOf = (i: number) => {
    if (heads[i] === undefined) heads[i] = findHead(slice, ridges[i].v, ridges[i].z, 0.06);
    return heads[i]!;
  };
  let best: { a: RailHit; b: RailHit; score: number } | null = null;
  for (let i = 0; i < ridges.length; i++) {
    for (let j = 0; j < ridges.length; j++) {
      const spacing0 = ridges[j].v - ridges[i].v;
      if (spacing0 < MIN_HEAD_SPACING_M - 0.05 || spacing0 > MAX_HEAD_SPACING_M + 0.05) continue;
      if (Math.abs(ridges[i].z - ridges[j].z) > 0.25) continue;
      const a = headOf(i);
      const b = headOf(j);
      if (!a || !b) continue;
      const spacing = b.v - a.v;
      if (spacing < MIN_HEAD_SPACING_M || spacing > MAX_HEAD_SPACING_M) continue;
      if (a.v > 0.15 || b.v < -0.15) continue;
      const tight = (a.points + b.points) / (a.spread + b.spread + 0.02);
      const standard = Math.exp(-(((spacing - headSpacingM) / 0.1) ** 2));
      const score = tight * Math.min(a.prominence, b.prominence) * (0.5 + standard);
      if (!best || score > best.score) best = { a, b, score };
    }
  }
  return best;
}

/** Where along a candidate heading the heads must be found again (m from the click). */
const CONTINUE_AT_M = [-1, 1] as const;
/** Coarse-sweep peaks checked for that. */
const CONTINUE_CANDIDATES = 6;
/** Of the 4 heads looked for (2 rails, 1 m either side), found again: 3 (one may lie in a gap of the scan). */
const CONTINUE_MIN_SHARE = 0.75;

/**
 * Find the track at a click: the heading whose cross-section shows the
 * sharpest head pair with the click on a head or between the two. Ridges are
 * sharpest when the slice is square to the rails (any other heading smears a
 * metre-long slice of each head across the profile). Coarse 2° sweep, then
 * 0.25° around the best.
 *
 * One slice can be fooled: short, dense clutter at the click cut along its
 * length (a kerb, a trough's edges, a fence) shows a crisper "pair" than the
 * rails, and the follower then sets off across the track (2026-10-27, five
 * clicks of six on one rail of the GYSEV scan). Rails go on, clutter does
 * not: of the best sweep peaks, one whose heads are found again 1 m ahead
 * and 1 m behind, where they should be, wins over any that are not.
 */
export function findTrack(
  grid: PointGrid,
  click: XYZ,
  /** The head spacing to prefer: the track gauge set (`TraceOptions.gaugeM`) + a head width; standard gauge by default. */
  headSpacingM = STANDARD_HEAD_SPACING_M,
): TrackFix | null {
  const halfWidth = 2.2;
  const zMin = click.z - 0.8;
  const zMax = click.z + 0.5;
  const reach = Math.max(...CONTINUE_AT_M.map(Math.abs)) + 0.6;
  // Read once: every trial slice (and the refining and continuing ones) lies within this square.
  const nb = neighbourhood(grid, click, Math.hypot(reach, halfWidth) + 1e-6, zMin, zMax);
  const headingAt = (deg: number): Heading => {
    const a = (deg * Math.PI) / 180;
    return { dx: Math.cos(a), dy: Math.sin(a) };
  };
  const trial = (deg: number) => {
    const heading = headingAt(deg);
    const slice = sliceOf(nb, heading, 0.6, halfWidth);
    return { heading, pair: bestPair(slice, halfWidth, headSpacingM) };
  };
  /** Share (0..1) of the pair's heads found again along the heading, within 4 cm of where they were. */
  const continues = (heading: Heading, pair: { a: RailHit; b: RailHit }) => {
    let found = 0;
    for (const u of CONTINUE_AT_M) {
      const slice = sliceOf(nb, heading, 0.3, halfWidth, u);
      for (const h of [pair.a, pair.b]) {
        const g = findHead(slice, h.v, h.z, 0.05);
        if (g && Math.abs(g.v - h.v) <= 0.04) found++;
      }
    }
    return found / (2 * CONTINUE_AT_M.length);
  };
  const sweep: number[] = [];
  for (let d = 0; d < 180; d += 2) sweep.push(trial(d).pair?.score ?? 0);
  // The sweep's peaks (a heading beats both neighbours, wrapping at 180°), best first.
  const peaks = sweep
    .map((score, i) => ({ deg: i * 2, score }))
    .filter(
      ({ score }, i) =>
        score > 0 && score >= sweep[(i + sweep.length - 1) % sweep.length] && score >= sweep[(i + 1) % sweep.length],
    )
    .sort((p, q) => q.score - p.score)
    .slice(0, CONTINUE_CANDIDATES);
  if (peaks.length === 0) return null;
  const candidates = peaks.map(({ deg, score }) => {
    const { heading, pair } = trial(deg);
    return { deg, score, continues: pair ? continues(heading, pair) : 0 };
  });
  // A heading whose heads go on beats any crisper one-slice pair, however crisp (dense clutter can
  // outscore the rails 80 to 1); among those that go on, and when none does, the crispest.
  const goesOn = (c: { continues: number }) => c.continues >= CONTINUE_MIN_SHARE;
  const better = (p: (typeof candidates)[number], q: (typeof candidates)[number]) =>
    goesOn(q) !== goesOn(p) ? goesOn(q) : q.score > p.score;
  let bestDeg = candidates.reduce((p, q) => (better(p, q) ? q : p)).deg;
  let bestScore = trial(bestDeg).pair?.score ?? 0;
  for (let d = bestDeg - 2; d <= bestDeg + 2; d += 0.25) {
    const s = trial(d).pair?.score ?? 0;
    if (s > bestScore) {
      bestScore = s;
      bestDeg = d;
    }
  }
  const { heading, pair } = trial(bestDeg);
  if (!pair) return null;
  // Refine both heads with the windowed detector the follower uses.
  const slice = sliceOf(nb, heading, 0.5, halfWidth);
  const left = findHead(slice, pair.a.v, pair.a.z, 0.06) ?? null;
  const right = findHead(slice, pair.b.v, pair.b.z, 0.06) ?? null;
  if (!left || !right) return null;
  const { nx, ny } = rightOf(heading);
  const vMid = (left.v + right.v) / 2;
  return {
    centre: { x: click.x + nx * vMid, y: click.y + ny * vMid, z: (left.z + right.z) / 2 },
    heading,
    left: { ...left, v: left.v - vMid, edges: left.edges && [left.edges[0] - vMid, left.edges[1] - vMid] },
    right: { ...right, v: right.v - vMid, edges: right.edges && [right.edges[0] - vMid, right.edges[1] - vMid] },
    score: bestScore,
    candidates,
  };
}

/**
 * Level crossings. Where a road crosses the track its surface comes up flush
 * with the rail tops, so no head stands clear of what is beside it and
 * `findHead` finds nothing — for as long as the road is wide (29 m on the
 * GYSEV scan's skewed crossing at 618 190 / 5 282 425, longer than the
 * follower's 20 m gap allowance). What does show is the flangeway: a groove
 * 4-8 cm wide and 3-5 cm deep along the gauge (inner) face of each rail, its
 * outer edge being the head's inner face. Measured there (2026-10-27): the
 * groove at |v| 0.66-0.72 m beside heads centred at |v| 0.754 m, 35-45 mm
 * deep, the road within ±10 mm of the rail tops, a few bins of the far
 * groove empty in the scanner's shadow.
 */
export interface GrooveHit {
  /** Across-track centre of the groove (m, slice frame). */
  v: number;
  /** Its edge on the rail's side: the head's inner (gauge) face (m). */
  faceV: number;
  /** Groove width (m). */
  width: number;
  /** Depth below the surface beside it (m). */
  depth: number;
  /** Height of the surface beside the groove: the rail top, near enough (m). */
  z: number;
  points: number;
}

const GROOVE_BIN_M = 0.01;
/** A flangeway is at least this deep below the surface either side of it (m). */
const MIN_GROOVE_DEPTH_M = 0.015;
const MIN_GROOVE_WIDTH_M = 0.03;
const MAX_GROOVE_WIDTH_M = 0.1;
/** The surface either side of a groove: these bins beyond its edges (m). */
const GROOVE_BESIDE_M = 0.05;

/**
 * The flangeway groove of a rail whose head's inner face is predicted at
 * `faceV0` (the groove lies on the side of `towardCentre`, -1 or +1), within
 * ±`searchM` across and ±`dzM` around `z0`. Null when there is none.
 */
export function findGroove(
  slice: Slice,
  faceV0: number,
  towardCentre: 1 | -1,
  z0: number,
  searchM = 0.12,
  dzM = 0.15,
): GrooveHit | null {
  const lo = faceV0 - searchM - MAX_GROOVE_WIDTH_M - GROOVE_BESIDE_M;
  const hi = faceV0 + searchM + MAX_GROOVE_WIDTH_M + GROOVE_BESIDE_M;
  const nb = Math.ceil((hi - lo) / GROOVE_BIN_M);
  const per: number[][] = Array.from({ length: nb }, () => []);
  for (let i = 0; i < slice.n; i++) {
    const v = slice.v[i];
    if (v < lo || v >= hi || Math.abs(slice.z[i] - z0) > dzM) continue;
    per[Math.floor((v - lo) / GROOVE_BIN_M)].push(slice.z[i]);
  }
  // Per bin, the median (a groove's floor is many points; a stray return is one).
  const med = per.map((zs) => (zs.length >= 2 ? median(zs) : NaN));
  const binV = (b: number) => lo + (b + 0.5) * GROOVE_BIN_M;
  const besideBins = Math.round(GROOVE_BESIDE_M / GROOVE_BIN_M);
  const surfaceOf = (from: number, to: number) => {
    const zs: number[] = [];
    for (let b = Math.max(0, from); b <= Math.min(nb - 1, to); b++) if (Number.isFinite(med[b])) zs.push(med[b]);
    return zs.length >= 2 ? median(zs) : NaN;
  };
  let best: GrooveHit | null = null;
  let bestScore = -Infinity;
  for (let a = 0; a < nb; a++) {
    if (!Number.isFinite(med[a])) continue;
    // Grow a run of low bins from a (an empty bin inside the run is the scanner's shadow).
    for (let b = a; b < nb && (b - a + 1) * GROOVE_BIN_M <= MAX_GROOVE_WIDTH_M; b++) {
      const width = (b - a + 1) * GROOVE_BIN_M;
      if (width < MIN_GROOVE_WIDTH_M || !Number.isFinite(med[b])) continue;
      const left = surfaceOf(a - besideBins, a - 1);
      const right = surfaceOf(b + 1, b + besideBins);
      if (!Number.isFinite(left) || !Number.isFinite(right)) continue;
      // The road and the rail top either side are level with each other.
      if (Math.abs(left - right) > 0.025) continue;
      const inside: number[] = [];
      let points = 0;
      for (let k = a; k <= b; k++) {
        if (!Number.isFinite(med[k])) continue;
        inside.push(med[k]);
        points += per[k].length;
      }
      if (inside.length * 2 < b - a + 1) continue;
      const surface = Math.min(left, right);
      const floor = median(inside);
      const depth = surface - floor;
      if (depth < MIN_GROOVE_DEPTH_M) continue;
      // Every filled bin of the run is clearly below the surface (else the run is wider than the groove).
      let allLow = true;
      for (let k = a; k <= b && allLow; k++)
        if (Number.isFinite(med[k]) && med[k] > surface - MIN_GROOVE_DEPTH_M / 2) allLow = false;
      if (!allLow) continue;
      const v = (binV(a) + binV(b)) / 2;
      // The edge on the rail's side (away from the centre).
      const faceV = towardCentre > 0 ? binV(a) - GROOVE_BIN_M / 2 : binV(b) + GROOVE_BIN_M / 2;
      if (Math.abs(faceV - faceV0) > searchM) continue;
      const score = depth * width - Math.abs(faceV - faceV0) * 0.01;
      if (score > bestScore) {
        bestScore = score;
        // The head is level with the surface on its side of the groove.
        const headSide = towardCentre > 0 ? left : right;
        best = { v, faceV, width, depth, z: headSide, points };
      }
    }
  }
  return best;
}

/**
 * A road surface across the track at about rail-top height: the slice is
 * covered across ±`halfWidth` (at least 70 % of its 5 cm bins hold points),
 * level (the bins' middle 80 % within 6 cm) and within 6 cm of `z0`. Ballast
 * lies 10-20 cm under the rail tops and is rough, so it never passes; no
 * points (the end of the scan) never pass either.
 */
export function isRoadSurface(slice: Slice, z0: number, halfWidth = 1.3): boolean {
  const BIN = 0.05;
  const nb = Math.ceil((2 * halfWidth) / BIN);
  const per: number[][] = Array.from({ length: nb }, () => []);
  for (let i = 0; i < slice.n; i++) {
    const b = Math.floor((slice.v[i] + halfWidth) / BIN);
    if (b >= 0 && b < nb && Math.abs(slice.z[i] - z0) <= 0.4) per[b].push(slice.z[i]);
  }
  const tops: number[] = [];
  for (const zs of per) if (zs.length >= 3) tops.push(median(zs));
  if (tops.length < nb * 0.7) return false;
  const s = sortedOf(tops);
  const mid = quantile(s, 0.5);
  return quantile(s, 0.9) - quantile(s, 0.1) <= 0.06 && Math.abs(mid - z0) <= 0.06;
}
